/**
 * The clinic (docs/ux/clinic.md): the week's appointments as a list or a
 * week grid, with the door words (confirmed, consent, questionnaire, doctor
 * allocation) and the marks, filtered by clinic and doctor. Every row opens
 * the matter hub. Two moves are offered from the page (OPEN 80, closed by
 * D-0066): booking a matter that waits at 4 (the hub's form, opened here)
 * and marking attendance on the appointment's day. The flags on a row are
 * the flag menu (D-0066), so Urgent is set or cleared without opening the
 * case. The week view is a column per clinic over Monday to Friday; there is
 * no weekend (D-0066). The phone shows cards with a day strip in place of
 * the week grid.
 */

import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { createContext, use, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { say } from '@/app/announcer';
import { useOnline } from '@/app/online';
import type { ClinicRow } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { FlagMenu } from '@/components/matter/FlagMenu';
import { StatusChip } from '@/components/matter/StatusChip';
import { FilterBar } from '@/components/shell/FilterBar';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { PageHeader } from '@/components/shell/PageHeader';
import { QueueTable } from '@/components/shell/QueueTable';
import { QueueTabs } from '@/components/shell/QueueTabs';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { useShellWidth } from '@/components/shell/context';
import { Button, buttonVariants } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AwaitingBooking } from './AwaitingBooking';
import { ATTENDANCE_WORD, CONSENT_WORD, QUESTIONNAIRE_WORD, addDays, canMarkAttended, clinicLanes, confirmedWord, dayHeading, dayKey, isThisWeek, mondayOf, timeOf, weekKey, weekLabel, weekdays } from './describe';
import { activeClinicFilters, applyClinicFilters, useClinicFilters, useClinicMove, useClinicWeek, type ClinicFilters } from './useClinic';

const hubHref = (ref: string) => `/matters/${encodeURIComponent(ref)}`;

type Attend = { onAttend: (row: ClinicRow) => void; busyRef: string | null; offline: boolean };

/**
 * The Attended move reaches the cells through context rather than through
 * the column definitions: a cell renderer closed over changing state is a
 * new component type to the table on every render, so every cell would
 * remount (and an open flag menu close) each time the week re-read.
 */
const AttendContext = createContext<Attend | null>(null);

/**
 * "Attended" from the row, on the day: the hub's move, read-before-write,
 * from the clinic (OPEN 80, closed by D-0066). In the 46 px row it is the
 * 24 px `xs` button so the flag line fits beneath it.
 */
function AttendButton({ row, size }: { row: ClinicRow; size: 'row' | 'card' }) {
  const attend = use(AttendContext);
  if (!attend || !canMarkAttended(row)) return null;
  const busy = attend.busyRef === row.matterRef;
  return (
    <Button
      type="button"
      variant="outline"
      size={size === 'card' ? 'sm' : 'xs'}
      data-slot="attend"
      className={size === 'card' ? 'h-9 self-start' : 'self-start'}
      disabled={attend.offline || attend.busyRef !== null}
      aria-busy={busy}
      aria-label={`Attended, ${row.matterRef}`}
      onClick={(e) => { e.stopPropagation(); attend.onAttend(row); }}
    >
      {busy ? 'Marking' : 'Attended'}
    </Button>
  );
}

function WhenCell({ row }: { row: ClinicRow }) {
  const d = new Date(row.startsAt);
  return <span className="tabular whitespace-nowrap">{dayHeading(d)} {timeOf(row.startsAt)}</span>;
}

/**
 * The row's mark: the attendance word (or, on the day, the Attended button),
 * the appointment's own fact, and the case's flags as the flag menu
 * (D-0066), so Urgent is set or cleared here without opening the case. In a
 * 46 px row the attendance takes one line and the flags a second, one flag
 * word then "+n" with the menu holding every flag, so a third line never
 * opens; a card runs the words inline, and the phone's trigger is a 44 px
 * target like the card's other controls.
 */
function Marks({ row, stack, offerAttend, phone }: { row: ClinicRow; stack?: boolean; offerAttend?: boolean; phone?: boolean }) {
  const attend = use(AttendContext);
  const attendance = row.attendance ? ATTENDANCE_WORD[row.attendance] : null;
  const button = Boolean(offerAttend && attend && !attendance && canMarkAttended(row));
  if (stack) {
    return (
      <>
        <span className="flex flex-wrap items-center gap-x-2 text-xs font-medium text-beam-error" data-slot="marks">
          {attendance ? <span>{attendance}</span> : null}
          <FlagMenu matterRef={row.matterRef} flags={row.flags} max={9} layout="inline" className={phone ? 'min-h-11' : undefined} />
        </span>
        {button ? <AttendButton row={row} size="card" /> : null}
      </>
    );
  }
  // The 24 px button and the 20 px flag line share the 46 px row; with the attendance word (16 px) or alone the trigger keeps its 28 px target.
  return (
    <span className="flex flex-col items-start text-xs leading-4 font-medium text-beam-error" data-slot="marks">
      {attendance ? <span>{attendance}</span> : button ? <AttendButton row={row} size="row" /> : null}
      <FlagMenu matterRef={row.matterRef} flags={row.flags} max={1} layout="inline" className={button ? 'min-h-5' : undefined} />
    </span>
  );
}

/** One constant, so the table never sees a new cell renderer and the cells keep their state (an open flag menu, focus) across re-reads. */
const COLUMNS: ColumnDef<ClinicRow, unknown>[] = [
  { id: 'when', accessorKey: 'startsAt', header: 'When', meta: { priority: 1, width: 136 }, cell: ({ row }) => <WhenCell row={row.original} /> },
  { id: 'patient', accessorKey: 'patientAsOnLoi', header: 'Patient (as on LOI)', meta: { priority: 1 }, cell: ({ row }) => <span className="block truncate" title={row.original.patientAsOnLoi}>{row.original.patientAsOnLoi}</span> },
  { id: 'matter', accessorKey: 'matterRef', header: 'Case', meta: { priority: 1, width: 132 }, cell: ({ row }) => <span className="tabular font-semibold">{row.original.matterRef}</span> },
  { id: 'doctor', accessorFn: (r) => r.doctor.name, header: 'Doctor', meta: { priority: 1, width: 140 }, cell: ({ row }) => <span className="block truncate">{row.original.doctor.name}</span> },
  { id: 'clinic', accessorFn: (r) => r.clinic.name, header: 'Clinic', meta: { priority: 3, width: 118 }, cell: ({ row }) => <span className="block truncate">{row.original.clinic.name}</span> },
  { id: 'confirmed', accessorKey: 'confirmed', header: 'Confirmed', enableSorting: false, meta: { priority: 1, width: 104 }, cell: ({ row }) => <span className={row.original.confirmed ? '' : 'text-beam-warning font-medium'}>{confirmedWord(row.original.confirmed)}</span> },
  { id: 'consent', accessorKey: 'consent', header: 'Consent', enableSorting: false, meta: { priority: 1, width: 88 }, cell: ({ row }) => <span className={row.original.consent === 'none' ? 'text-beam-text-muted' : ''}>{CONSENT_WORD[row.original.consent]}</span> },
  { id: 'questionnaire', accessorKey: 'questionnaire', header: 'Questionnaire', enableSorting: false, meta: { priority: 2, width: 120 }, cell: ({ row }) => <span className={row.original.questionnaire === 'none' ? 'text-beam-text-muted' : ''}>{QUESTIONNAIRE_WORD[row.original.questionnaire]}</span> },
  { id: 'allocation', accessorKey: 'drAllocationConfirmed', header: 'Dr allocation', enableSorting: false, meta: { priority: 2, width: 118 }, cell: ({ row }) => (row.original.drAllocationConfirmed ? <span className="flex items-center gap-1"><CheckIcon aria-hidden="true" className="size-4 shrink-0 text-beam-success" />Confirmed</span> : <span className="text-beam-text-muted">Not yet</span>) },
  { id: 'mark', header: 'Mark', enableSorting: false, meta: { priority: 1, width: 142 }, cell: ({ row }) => <Marks row={row.original} offerAttend /> },
  { id: 'open', header: () => <span className="sr-only">Open</span>, enableSorting: false, meta: { priority: 1, width: 72, align: 'right' }, cell: ({ row }) => <Link to={hubHref(row.original.matterRef)} aria-label={`Open ${row.original.matterRef}`} className={buttonVariants({ variant: 'outline', size: 'default' })}>Open</Link> },
];

function WeekBar({ monday, onMove }: { monday: Date; onMove: (m: Date) => void }) {
  const phone = useIsPhone();
  const size = phone ? 'lg' : 'sm';
  return (
    <div data-slot="week-bar" className="flex flex-wrap items-center gap-2">
      <Button variant="outline" size={size} className={phone ? 'h-11' : undefined} aria-label={`Previous week, ${weekLabel(addDays(monday, -7))}`} onClick={() => onMove(addDays(monday, -7))}><ChevronLeftIcon aria-hidden="true" className="size-4" />Previous</Button>
      <span className="font-heading text-[18px] font-medium" aria-live="polite">{weekLabel(monday)}</span>
      <Button variant="outline" size={size} className={phone ? 'h-11' : undefined} disabled={isThisWeek(monday)} onClick={() => onMove(mondayOf(new Date()))}>This week</Button>
      <Button variant="outline" size={size} className={phone ? 'h-11' : undefined} aria-label={`Next week, ${weekLabel(addDays(monday, 7))}`} onClick={() => onMove(addDays(monday, 7))}>Next<ChevronRightIcon aria-hidden="true" className="size-4" /></Button>
    </div>
  );
}

function AppointmentCard({ row, offerAttend }: { row: ClinicRow; offerAttend?: boolean }) {
  // The phone's only way from /clinic into a matter (weekly review, 9 September 2026): a 44 px target, full width, like the other phone cards.
  const phone = useIsPhone();
  return (
    <article data-slot="appointment-card" className="flex flex-col gap-1 rounded-lg border border-beam-line bg-beam-paper p-3 text-sm">
      <span className="flex items-baseline justify-between gap-2"><span className="tabular whitespace-nowrap font-semibold">{timeOf(row.startsAt)}</span><span className="truncate text-xs text-beam-text-muted" title={row.clinic.name}>{row.clinic.name}</span></span>
      <span className="truncate">{row.patientAsOnLoi}</span>
      <span className="truncate text-[13px] text-beam-text-muted">{row.doctor.name}</span>
      <span className={`text-xs ${row.confirmed ? 'text-beam-text-muted' : 'font-medium text-beam-warning'}`}>{confirmedWord(row.confirmed)}</span>
      <Marks row={row} stack offerAttend={offerAttend} phone={phone} />
      <Link to={hubHref(row.matterRef)} aria-label={`Open ${row.matterRef}, ${timeOf(row.startsAt)}, ${row.doctor.name}`} className={buttonVariants({ variant: 'outline', size: phone ? 'lg' : 'sm' }) + (phone ? ' mt-1 h-11 w-full' : ' mt-1 self-start')}>Open</Link>
    </article>
  );
}

/**
 * The week as swim lanes (D-0066): one column per clinic in alphabetical
 * order, one row band per weekday, the appointments of that clinic on that
 * day stacked in time order in the cell. A clinic with nothing this week has
 * no lane; the Clinic filter narrows to one lane.
 */
function WeekGrid({ monday, rows }: { monday: Date; rows: ClinicRow[] }) {
  const days = weekdays(monday);
  // Lanes come from the weekday rows only: a weekend appointment, should one be keyed, sits in the List view (D-0066).
  const weekdayKeys = new Set(days.map(weekKey));
  const lanes = clinicLanes(rows.filter((r) => weekdayKeys.has(dayKey(r.startsAt))));
  return (
    <div className="overflow-x-auto rounded-xl border border-beam-line bg-beam-paper">
      <table data-slot="week-grid" className="w-full table-fixed text-sm">
        <caption className="sr-only">{weekLabel(monday)}, Monday to Friday, one column per clinic</caption>
        <thead>
          <tr className="border-b border-beam-line bg-beam-cream-deep">
            <th scope="col" className="eyebrow w-28 px-3 py-2 text-left text-beam-text-soft">Day</th>
            {lanes.length === 0 ? <th scope="col" className="eyebrow px-3 py-2 text-left text-beam-text-soft">No clinics this week</th> : lanes.map((c) => <th key={c.id} scope="col" data-clinic={c.id} className="eyebrow px-3 py-2 text-left text-beam-text-soft">{c.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const key = weekKey(d);
            return (
              <tr key={key} className="border-b border-beam-line align-top last:border-b-0" data-day={key}>
                <th scope="row" className="tabular whitespace-nowrap px-3 py-2 text-left text-sm font-medium text-beam-text-soft">{dayHeading(d)}</th>
                {lanes.length === 0 ? <td className="p-2 text-xs text-beam-text-muted">No appointments</td> : lanes.map((c) => {
                  const cell = rows.filter((r) => dayKey(r.startsAt) === key && r.clinic.id === c.id);
                  return (
                    <td key={c.id} data-day={key} data-clinic={c.id} className="border-l border-beam-line p-2">
                      <div className="flex min-h-12 flex-col gap-2">
                        {cell.length === 0 ? <span className="sr-only">No appointments</span> : cell.map((r) => <AppointmentCard key={r.apptId} row={r} />)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DayStrip({ monday, rows, day, onPick }: { monday: Date; rows: ClinicRow[]; day: string | null; onPick: (day: string | null) => void }) {
  const days = weekdays(monday);
  return (
    <div data-slot="day-strip" className="grid grid-cols-5 gap-1">
      {days.map((d) => {
        const key = weekKey(d);
        const n = rows.filter((r) => dayKey(r.startsAt) === key).length;
        const pressed = day === key;
        return (
          <button key={key} type="button" aria-pressed={pressed} className={`flex h-11 flex-col items-center justify-center rounded-lg border text-xs ${pressed ? 'border-beam-signature bg-beam-cream-deep' : 'border-beam-line bg-beam-paper'}`} onClick={() => onPick(pressed ? null : key)}>
            <span>{dayHeading(d)}</span>
            <span className="tabular text-beam-text-muted">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ClinicPage() {
  useShellWidth('wide');
  const phone = useIsPhone();
  const online = useOnline();
  const { filters, update } = useClinicFilters();
  const week = useClinicWeek(filters.week);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<ClinicFilters>(filters);
  const all = week.data?.rows ?? [];
  const visible = useMemo(() => applyClinicFilters(all, filters).filter((r) => !phone || !filters.day || dayKey(r.startsAt) === filters.day), [all, filters, phone]);
  const clinics = useMemo(() => [...new Map(all.map((r) => [r.clinic.id, r.clinic.name])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [all]);
  const doctors = useMemo(() => [...new Map(all.map((r) => [r.doctor.slug, r.doctor.name])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [all]);
  const attendMove = useClinicMove();
  const busyRef = attendMove.isPending ? attendMove.variables?.ref ?? null : null;
  const attend: Attend = useMemo(() => ({
    onAttend: (row) => attendMove.mutate({ ref: row.matterRef, event: 'attended' }),
    busyRef,
    offline: !online,
  }), [attendMove.mutate, busyRef, online]);
  const sorting: SortingState = [{ id: 'when', desc: false }];
  const move = (m: Date) => { say(weekLabel(m)); update({ week: m, day: null }); };
  const tabs = [{ value: 'list', label: 'List', ...(week.data ? { count: applyClinicFilters(all, filters).length } : {}) }, { value: 'week', label: 'Week' }];
  const header = <PageHeader title="Clinic" description="Appointments for the week, by day." tabs={phone ? undefined : <QueueTabs tabs={tabs} value={filters.view} onChange={(v) => update({ view: v as ClinicFilters['view'] })} label="Clinic views" panelId="clinic-view-panel" />} />;

  if (week.isError) {
    return <div className="flex flex-col gap-3">{header}<WeekBar monday={filters.week} onMove={move} /><ErrorPanel message="The clinic could not be loaded." onRetry={() => void week.refetch()} offline={!online} /></div>;
  }
  const label = weekLabel(filters.week).toLowerCase();
  const empty = all.length === 0 ? (
    <EmptyState title={`No appointments in the ${label}.`} action={isThisWeek(filters.week) ? undefined : <Button variant="outline" onClick={() => move(mondayOf(new Date()))}>This week</Button>} />
  ) : (
    <EmptyState title={`No appointments${filters.doctor ? ` for ${doctors.find((d) => d[0] === filters.doctor)?.[1] ?? 'this doctor'}` : ''}${filters.clinic ? ` at ${clinics.find((c) => c[0] === filters.clinic)?.[1] ?? 'this clinic'}` : ''}${filters.day ? ' on that day' : ''} this week.`} action={<Button variant="link" className="h-auto p-0" onClick={() => update({ clinic: null, doctor: null, day: null })}>Clear filters</Button>} />
  );

  return (
    <AttendContext value={attend}>
    <div className="flex flex-col gap-3">
      {header}
      <WeekBar monday={filters.week} onMove={move} />
      <FilterBar
        activeCount={activeClinicFilters(filters)}
        hint={<span role="status" data-slot="results-line">{week.isPending ? 'Loading the week' : `Showing ${visible.length} of ${all.length}`}</span>}
        sheetOpen={sheetOpen}
        onSheetOpenChange={(open) => { if (open) setDraft(filters); setSheetOpen(open); }}
        onApply={() => { update({ clinic: draft.clinic, doctor: draft.doctor }); setSheetOpen(false); }}
        onClear={() => { update({ clinic: null, doctor: null, day: null }); setSheetOpen(false); }}
        desktop={
          <>
            <Select value={filters.clinic ?? 'all'} onValueChange={(v) => update({ clinic: v === 'all' ? null : v })}>
              <SelectTrigger aria-label="Clinic" className="h-9 min-w-40 bg-beam-paper"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Clinic: all</SelectItem>{clinics.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.doctor ?? 'all'} onValueChange={(v) => update({ doctor: v === 'all' ? null : v })}>
              <SelectTrigger aria-label="Doctor" className="h-9 min-w-40 bg-beam-paper"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Doctor: all</SelectItem>{doctors.map(([slug, name]) => <SelectItem key={slug} value={slug}>{name}</SelectItem>)}</SelectContent>
            </Select>
          </>
        }
        sheet={
          <div className="flex flex-col gap-5">
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium">Clinic</legend>
              <RadioGroup value={draft.clinic ?? 'all'} onValueChange={(v) => setDraft({ ...draft, clinic: v === 'all' ? null : v })}>
                <div className="flex min-h-11 items-center gap-3"><RadioGroupItem value="all" id="clinic-all" /><Label htmlFor="clinic-all">All clinics</Label></div>
                {clinics.map(([id, name]) => <div key={id} className="flex min-h-11 items-center gap-3"><RadioGroupItem value={id} id={`clinic-${id}`} /><Label htmlFor={`clinic-${id}`}>{name}</Label></div>)}
              </RadioGroup>
            </fieldset>
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium">Doctor</legend>
              <RadioGroup value={draft.doctor ?? 'all'} onValueChange={(v) => setDraft({ ...draft, doctor: v === 'all' ? null : v })}>
                <div className="flex min-h-11 items-center gap-3"><RadioGroupItem value="all" id="doctor-all" /><Label htmlFor="doctor-all">All doctors</Label></div>
                {doctors.map(([slug, name]) => <div key={slug} className="flex min-h-11 items-center gap-3"><RadioGroupItem value={slug} id={`doctor-${slug}`} /><Label htmlFor={`doctor-${slug}`}>{name}</Label></div>)}
              </RadioGroup>
            </fieldset>
          </div>
        }
      />
      <AwaitingBooking doctor={filters.doctor} offline={!online} />
      <div id="clinic-view-panel" role="tabpanel" aria-label={phone ? 'Appointments' : filters.view === 'week' ? 'Week' : 'List'}>
      {phone ? (
        <>
          <DayStrip monday={filters.week} rows={applyClinicFilters(all, filters)} day={filters.day} onPick={(day) => update({ day })} />
          {week.isPending ? <Skeleton className="h-40 rounded-xl" /> : visible.length === 0 ? empty : <div className="flex flex-col gap-3" data-slot="appointment-cards">{visible.map((r) => <AppointmentCard key={r.apptId} row={r} offerAttend />)}</div>}
        </>
      ) : filters.view === 'week' ? (
        week.isPending ? <Skeleton className="h-64 rounded-xl" /> : <WeekGrid monday={filters.week} rows={applyClinicFilters(all, filters)} />
      ) : (
        <QueueTable
          columns={COLUMNS}
          data={visible}
          sorting={sorting}
          onSortingChange={() => undefined}
          getRowId={(r) => r.apptId}
          rowHref={(r) => hubHref(r.matterRef)}
          rowState={(r) => (r.attendance === 'dna' || r.attendance === 'cancelled' ? 'overdue' : 'default')}
          caption={`${weekLabel(filters.week)}: ${visible.length} ${visible.length === 1 ? 'appointment' : 'appointments'} in time order`}
          state={week.isPending ? 'loading' : 'ready'}
          empty={empty}
        />
      )}
      </div>
      <Legend>
        <LegendItem term={<span className="font-medium">Confirmed</span>}>the patient has confirmed the appointment; "Not confirmed" is the nurse's call list.</LegendItem>
        <LegendItem term={<span className="font-medium">Consent</span>}>returned, in clinic (signed at the door), sent, or none; keyed as side-steps until VitalSign syncs them.</LegendItem>
        <LegendItem term={<span className="font-medium">Dr allocation</span>}>the doctor has confirmed the allocation.</LegendItem>
        <LegendItem term={<StatusChip status={5} size="sm" />}>waiting on the appointment; Attended (offered on the row from the appointment's day) moves the case to the doctor, Record DNA to 14, Cancel to 15 from the hub.</LegendItem>
        <LegendItem term={<span className="font-medium">Awaiting booking</span>}>cases at 4 that need an appointment; Book keys it here, as the hub does.</LegendItem>
        <LegendItem term={<span className="font-medium">Flags</span>}>the case's flags; click them to see every flag and to set or clear Urgent without opening the case.</LegendItem>
        <LegendItem term={<span className="font-medium text-beam-error">Red rows</span>}>the appointment was not kept: did not attend, or cancelled.</LegendItem>
      </Legend>
    </div>
    </AttendContext>
  );
}
